import React, { useState, useContext, useMemo } from 'react';
import { QueryContext, UserContext } from '../../hooks/Contexts';
import { useNavigate } from 'react-router-dom';
import { queryFormForSub } from './constants/handleSubmission';
import Icon from '../common/Icon';
import SendBatchMailerModal from '../mailers/SendBatchMailerModal';
import CountExportModal from './exports/CountExportModal';
import BatchUpdateModal from './batch-update/BatchUpdateModal';
import SubmissionExportModal from './exports/SubmissionExportModal';
import LinkedFilterModal from './table-components/linked-submissions-filter/LinkedFilterModal';

const SubIndexControls = ({ form, clearFilters, meta }) => {
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const {
    queryParams,
    setQueryParams,
    expandable,
    expanded,
    handleExpand,
    filteringByExpanded,
  } = useContext(QueryContext);
  const [passwordFilter, setPasswordFilter] = useState(
    queryParams?.course_code && queryParams.course_code == 'password'
      ? true
      : false
  );
  const [viewMailSend, setViewMailSend] = useState(false);
  const [viewSubExport, setViewSubExport] = useState(false);
  const [viewCountExport, setViewCountExport] = useState(false);
  const [viewBatchUpdate, setViewBatchUpdate] = useState(false);
  const [viewLinkedFilter, setViewLinkedFilter] = useState(false);
  const [formExports, setFormExports] = useState(form.exports);
  const [subSearchErrors, setSubSearchErrors] = useState({
    message: '',
    type: '',
  });

  const { total_records, quota_count, filtering_by_informant, filters } = meta;

  const filterKeys = Object.keys(filters);
  const countFiltered = filterKeys.length ? true : false;
  const universalMailers = form.mailers.filter((m) => m.universal_mail_send);
  const formLinked = form.is_parent || form.submission_validate;
  const linkedFilterActive =
    filterKeys.includes('parent_id') || filterKeys.includes('has_group');
  const showMailerButton = user.canWrite || universalMailers.length > 0;
  const selectableMailers = user.canWrite ? form.mailers : universalMailers;
  const globalInformantField = useMemo(() => {
    return (
      form.fieldsets
        ?.flatMap((fs) => fs.fields)
        .find((field) => field?.informs_global) || null
    );
  }, [form]);

  async function goToSub(e) {
    if (e.key === 'Enter' && e.target.value) {
      let subId = e.target.value;
      let validInput = /^[1-9][0-9]*$/;

      if (validInput.test(subId)) {
        let res = await queryFormForSub(form.id, { subId });
        if (res.data.proceed) {
          navigate(`/manage/submissions/${res.data.submission_id}`);
        } else {
          setSubSearchErrors({
            message: res.data?.errors || 'Unknown error',
            type: 'id',
          });
        }
      } else {
        setSubSearchErrors({ message: 'Invalid Submission ID.', type: 'id' });
      }
    }
  }

  async function goToSubByCourseCode(e) {
    if (e.key === 'Enter') {
      let courseCode = e.target.value;

      if (courseCode) {
        let res = await queryFormForSub(form.id, { courseCode });
        if (res.data.proceed) {
          navigate(`/manage/submissions/${res.data.submission_id}`);
        } else {
          setSubSearchErrors({
            message: res.data?.errors || 'Unknown error',
            type: 'code',
          });
        }
      }
    }
  }

  const togglePasswordFilter = () => {
    setQueryParams((params) => {
      return { ...params, course_code: !passwordFilter ? 'password' : '' };
    });
    setPasswordFilter(!passwordFilter);
  };

  const refreshSubmissions = () => {
    setPasswordFilter(false);
    setSubSearchErrors({ message: '', type: '' });
    clearFilters();
    $('.fa-arrows-rotate').addClass('fa-spin');
    setTimeout(function () {
      $('.fa-arrows-rotate').removeClass('fa-spin');
    }, 1000);
  };

  return (
    <>
      <h2 className='left'>Submissions</h2>
      <div className='controls left'>
        {subSearchErrors.message && (
          <span className='error-span'>{subSearchErrors.message}</span>
        )}

        <span className='search-control'>
          <input
            type='text'
            placeholder='ID'
            className={subSearchErrors.type == 'id' ? 'highlight' : ''}
            onKeyDown={(e) => goToSub(e)}
            onChange={(e) => {
              if (!e.target.value)
                setSubSearchErrors({ message: '', type: '' });
            }}
          />
        </span>

        <Icon
          className='go-control'
          title='Refresh Submissions'
          icon='arrows-rotate'
          onClick={refreshSubmissions}
        />

        {form.course_validate && (
          <>
            <span className='search-control'>
              <input
                type='text'
                placeholder='Course Code'
                className={subSearchErrors.type == 'code' ? 'highlight' : ''}
                onKeyDown={(e) => goToSubByCourseCode(e)}
                onChange={(e) => {
                  if (!e.target.value)
                    setSubSearchErrors({ message: '', type: '' });
                }}
              />
            </span>
            <Icon
              className={`filter-btn ${passwordFilter ? 'active' : ''}`}
              title={
                passwordFilter
                  ? 'Remove "Password" Filter'
                  : 'Show "Password" Submissions'
              }
              icon='lock'
              onClick={togglePasswordFilter}
            />
          </>
        )}

        {formLinked && (
          <Icon
            className={linkedFilterActive ? 'filter-btn active' : 'filter-btn'}
            title={
              linkedFilterActive
                ? 'Link Filters Active'
                : 'Filters for Linked Submissions'
            }
            icon='link'
            onClick={() => setViewLinkedFilter(true)}
          />
        )}

        {expandable && (
          <a
            onClick={() => handleExpand()}
            className={
              filteringByExpanded && !expanded
                ? 'filter-btn active'
                : 'filter-btn'
            }
          >
            <i
              className={`fa-solid fa-${expanded ? 'down-left-and-up-right-to-center' : 'left-right'} icon-only`}
              style={expanded ? { transform: 'rotate(45deg)' } : {}}
              title={
                filteringByExpanded && !expanded
                  ? 'Filtering by hidden columns'
                  : `${expanded ? 'Hide' : 'Show'} extra columns`
              }
            />
          </a>
        )}
      </div>

      <div className='controls spaced'>
        <span className={countFiltered ? 'count filtered' : 'count'}>
          <b>Count:</b> {total_records}
        </span>
        <hr />

        <span className={filtering_by_informant ? 'quota filtered' : 'quota'}>
          <b>Quota:</b>

          {filtering_by_informant
            ? quota_count
            : form.global_quota || 'Not Set'}
        </span>

        {showMailerButton && (
          <Icon
            icon='envelope'
            title='Batch Mailer'
            onClick={() => setViewMailSend(true)}
          />
        )}
        <Icon
          icon='download'
          title='Export Submissions'
          onClick={() => setViewSubExport(true)}
        />
        <Icon
          icon='list-ol'
          title='Export Counts & Options'
          onClick={() => setViewCountExport(true)}
        />
        {user.canWrite && (
          <Icon
            icon='pen-to-square'
            title='Batch Update'
            onClick={() => setViewBatchUpdate(true)}
            faPrefix={'regular'}
          />
        )}
      </div>

      <SendBatchMailerModal
        filters={queryParams.filters}
        mailers={selectableMailers}
        viewMailSend={viewMailSend}
        setViewMailSend={setViewMailSend}
      />

      <SubmissionExportModal
        viewSubExport={viewSubExport}
        setViewSubExport={setViewSubExport}
        formExports={formExports}
        setFormExports={setFormExports}
        fieldsets={form.fieldsets}
        formID={form.id}
        allowParentOption={
          form.linked_parent_form_id && form.linked_parent_form_id > 0
        }
      />

      <CountExportModal
        viewCountExport={viewCountExport}
        setViewCountExport={setViewCountExport}
        fieldsets={form.fieldsets}
        formID={form.id}
        informant={globalInformantField}
      />

      <BatchUpdateModal
        viewBatchUpdate={viewBatchUpdate}
        setViewBatchUpdate={setViewBatchUpdate}
        fieldsets={form.fieldsets}
        userIsAdmin={user.isAdmin}
        submissionCount={meta.option_count || meta.total_records}
      />

      <LinkedFilterModal
        viewLinkedFilter={viewLinkedFilter}
        setViewLinkedFilter={setViewLinkedFilter}
      />
    </>
  );
};

export default SubIndexControls;
